import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import RouteMap, { buildNavUrl } from "../components/RouteMap";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Driver() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [myLoc, setMyLoc] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [queued, setQueued] = useState(0);
  const watchRef = useRef(null);
  const lastSent = useRef(0);
  const queueRef = useRef([]);
  const qKey = `rs_track_q_${token}`;

  const persistQueue = useCallback(() => {
    try { localStorage.setItem(qKey, JSON.stringify(queueRef.current.slice(-100))); } catch { /* ignore */ }
    setQueued(queueRef.current.length);
  }, [qKey]);

  const flushQueue = useCallback(async () => {
    if (!navigator.onLine) return;
    while (queueRef.current.length) {
      try {
        await axios.post(`${BASE}/track/${token}/ping`, queueRef.current[0]);
        queueRef.current.shift();
        persistQueue();
      } catch { break; }
    }
  }, [token, persistQueue]);

  const sendPoint = useCallback(async (pt) => {
    if (!navigator.onLine) { queueRef.current.push(pt); persistQueue(); return; }
    try {
      await axios.post(`${BASE}/track/${token}/ping`, pt);
      if (queueRef.current.length) flushQueue();
    } catch { queueRef.current.push(pt); persistQueue(); }
  }, [token, persistQueue, flushQueue]);

  useEffect(() => {
    let stop = false;
    try { queueRef.current = JSON.parse(localStorage.getItem(qKey) || "[]") || []; setQueued(queueRef.current.length); } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await axios.get(`${BASE}/track/${token}`);
        if (stop) return;
        setInfo(data);
        if (data.driverLocation) setMyLoc(data.driverLocation);
      } catch (e) { if (!stop) setErr(e.response?.data?.detail || "Link inválido ou expirado."); }
    })();
    return () => { stop = true; };
  }, [token, qKey]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flushQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [flushQueue]);

  const startShare = () => {
    if (!navigator.geolocation) return alert("Seu navegador não suporta geolocalização.");
    setSharing(true);
    watchRef.current = navigator.geolocation.watchPosition((pos) => {
      const { latitude, longitude, heading, speed, accuracy } = pos.coords;
      setMyLoc({ lat: latitude, lng: longitude, heading });
      const now = Date.now();
      if (now - lastSent.current > 8000) {
        lastSent.current = now;
        sendPoint({ lat: latitude, lng: longitude, heading, speed, accuracy, capturedAt: new Date().toISOString() });
      }
    }, () => {}, { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 });
  };
  const stopShare = () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); setSharing(false); };
  useEffect(() => () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); }, []);

  if (err) return <div className="driver-page"><div className="driver-body"><div className="driver-card"><h2 style={{ marginTop: 0 }}>Ops…</h2><p>{err}</p></div></div></div>;
  if (!info) return <div className="driver-page"><div className="driver-body"><div className="driver-card">Carregando percurso…</div></div></div>;

  const navUrl = buildNavUrl(info.addresses);

  return (
    <div className="driver-page">
      <div className="driver-header" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {info.companyLogo ? <img src={info.companyLogo} alt={info.company} style={{ height: 40, width: "auto", borderRadius: 6, background: "#fff", padding: 2 }} data-testid="driver-company-logo" /> : null}
        <div>
          <strong data-testid="driver-company-name">{info.company}</strong>
          {info.companyDoc ? <div style={{ fontSize: 12, opacity: 0.85 }} data-testid="driver-company-doc">CNPJ: {info.companyDoc}</div> : null}
          <div style={{ fontSize: 13, opacity: 0.85 }}>Rota de remoção — motorista</div>
        </div>
      </div>
      <div className="driver-body">
        <div className="driver-card">
          <a className="driver-nav-btn" href={navUrl} target="_blank" rel="noreferrer" data-testid="driver-open-navigation">Abrir navegação no Google Maps</a>
        </div>
        <div className="driver-card">
          <button className={`driver-share-btn ${sharing ? "driver-share-on" : "driver-share-off"}`} onClick={sharing ? stopShare : startShare} data-testid="driver-share-toggle">
            {sharing ? "Parar compartilhamento de localização" : "Compartilhar minha localização ao vivo"}
          </button>
          {sharing && (
            <p style={{ fontSize: 12, marginTop: 8, color: online ? "#2f7d4f" : "#c0392b" }} data-testid="driver-conn-status">
              {online
                ? (queued > 0 ? `Reenviando ${queued} ponto(s) guardado(s)…` : "● Sua localização está sendo enviada em tempo real.")
                : `⚠ Sem sinal de internet. Guardando sua localização (${queued} ponto[s]) — será reenviada automaticamente quando o sinal voltar.`}
            </p>
          )}
        </div>
        <div className="driver-card" style={{ padding: 0, overflow: "hidden" }}>
          <RouteMap points={info.points} polyline={info.polyline} driverLocation={myLoc} height={320} />
        </div>
        <div className="driver-card">
          <h3 style={{ marginTop: 0 }}>Percurso</h3>
          <p><b>Origem:</b> {info.addresses.origin || "—"}</p>
          <p><b>Paciente:</b> {info.patient ? info.patient + " — " : ""}{info.addresses.patient || "—"}</p>
          <p><b>Clínica:</b> {info.addresses.clinicName ? info.addresses.clinicName + " — " : ""}{info.addresses.clinic || "—"}</p>
          <p><b>Veículo:</b> {info.vehicle || "—"} • <b>Distância:</b> {info.km} km</p>
          {info.scheduledAt && <p><b>Saída prevista:</b> {info.scheduledAt}</p>}
        </div>
      </div>
    </div>
  );
}
